import * as React from "react";
import { prefixer } from "stylis";
import rtlPlugin from "@mui/stylis-plugin-rtl";
import createCache from "@emotion/cache";
import { CacheProvider } from "@emotion/react";
import { AdapterMomentJalaali } from "@mui/x-date-pickers/AdapterMomentJalaali";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { createTheme, ThemeProvider, useTheme } from "@mui/material/styles";
import { DatePicker } from "@mui/x-date-pickers";

const cacheRtl = createCache({
  key: "adapter-moment-jalali-demo",
  stylisPlugins: [prefixer, rtlPlugin],
});

interface AdapterMomentJalaliProps {
  handleChange?: (value: any) => void;
  valueDate?: any;
  sx?: React.CSSProperties;
  widthProps?: string | number | Record<string, string | number>;
  heightProps?: string | number | Record<string, string | number>;
  errorHandle?: { message?: string };
}

export default function AdapterMomentJalali({
  handleChange,
  valueDate,
  widthProps = "80%",
  sx,
  heightProps = "38px",
  errorHandle,
}: AdapterMomentJalaliProps): React.JSX.Element {
  const existingTheme = useTheme();
  const [open, setOpen] = React.useState(false);

  const theme = React.useMemo(
    () => createTheme(existingTheme, { direction: "rtl" }),
    [existingTheme],
  );

  const handleInputClick = () => {
    setOpen(true);
  };

  return (
    <CacheProvider value={cacheRtl}>
      <ThemeProvider theme={theme}>
        <div dir="rtl" style={{ ...sx }}>
          <LocalizationProvider dateAdapter={AdapterMomentJalaali}>
            <DatePicker
              open={open}
              onClose={() => setOpen(false)}
              onOpen={() => setOpen(true)}
              views={["year", "month", "day"]}
              openTo="year"
              value={valueDate}
              onChange={handleChange}
              sx={{
                width: widthProps,

                "& .MuiOutlinedInput-root": {
                  height: heightProps,
                  color: "#fff",
                  cursor: "pointer",

                  "& fieldset": {
                    borderColor: errorHandle?.message ? "#d32f2f" : "#444",
                  },

                  "&:hover fieldset": {
                    borderColor: errorHandle?.message ? "#d32f2f" : "#888",
                  },

                  "&.Mui-focused fieldset": {
                    borderColor: errorHandle?.message ? "#d32f2f" : "#00bcd4",
                  },
                },

                "& .MuiInputBase-input": {
                  color: "#fff",
                  padding: "8px 12px",
                  cursor: "pointer",
                },
                "& .MuiYearCalendar-button": {
                  fontSize: "14px !important",
                },
                "& .MuiInputAdornment-root .MuiSvgIcon-root": {
                  color: "#fff",
                },

                "& .MuiIconButton-root": {
                  color: "#fff",
                },

                "& .MuiPickersInputBase-root": {
                  backgroundColor: "#CFCFCF",
                  borderRadius: "8px",
                },
              }}
              slotProps={{
                textField: {
                  readOnly: true,
                  onClick: handleInputClick,
                  fullWidth: true,
                },

                desktopPaper: {
                  dir: "rtl",
                  sx: {
                    backgroundColor: "#CFCFCF",
                    color: "#fff",
                    border: "1px solid #333",
                    "& .MuiYearCalendar-button": {
                      fontSize: "14px !important",
                    },

                    "& .MuiMonthCalendar-button": {
                      fontSize: "14px !important",
                    },

                    "& .MuiPickersCalendarHeader-root": {
                      color: "#fff",
                    },

                    "& .MuiPickersCalendarHeader-label": {
                      color: "#fff",
                      fontSize: "14px",
                    },

                    "& .MuiIconButton-root": {
                      color: "#082A2E",
                    },

                    "& .MuiDayCalendar-weekDayLabel": {
                      color: "#aaa",
                    },

                    "& .MuiPickersDay-root": {
                      color: "#082A2E",
                      borderRadius: "8px",
                    },

                    "& .MuiPickersDay-root:hover": {
                      backgroundColor: "#aaa",
                    },

                    "& .Mui-selected": {
                      backgroundColor: "#00bcd4 !important",
                      color: "#fff !important",
                    },

                    "& .MuiPickersYear-yearButton": {
                      color: "#082A2E",
                      fontSize: "8px",
                    },

                    "& .MuiPickersYear-yearButton.Mui-selected": {
                      backgroundColor: "#00bcd4",
                      color: "#fff",
                    },

                    "& .MuiPickersMonth-monthButton": {
                      color: "#fff",
                      fontSize: "13px",
                    },

                    "& .MuiPickersMonth-monthButton.Mui-selected": {
                      backgroundColor: "#00bcd4",
                      color: "#fff",
                    },
                  },
                },

                mobilePaper: {
                  dir: "rtl",
                  sx: {
                    backgroundColor: "#CFCFCF",
                    color: "#fff",
                    border: "1px solid #333",
                    "& .MuiYearCalendar-button": {
                      fontSize: "14px !important",
                    },

                    "& .MuiMonthCalendar-button": {
                      fontSize: "14px !important",
                    },

                    "& .MuiPickersCalendarHeader-root": {
                      color: "#fff",
                    },

                    "& .MuiPickersCalendarHeader-label": {
                      color: "#fff",
                      fontSize: "14px",
                    },

                    "& .MuiIconButton-root": {
                      color: "#fff",
                    },

                    "& .MuiDayCalendar-weekDayLabel": {
                      color: "#aaa",
                    },

                    "& .MuiPickersDay-root": {
                      color: "#fff",
                    },

                    "& .MuiPickersDay-root:hover": {
                      backgroundColor: "#333",
                    },

                    "& .Mui-selected": {
                      backgroundColor: "#00bcd4 !important",
                      color: "#fff !important",
                    },

                    "& .MuiPickersYear-yearButton": {
                      color: "#fff",
                    },

                    "& .MuiPickersMonth-monthButton": {
                      color: "#fff",
                    },
                  },
                },

                popper: {
                  sx: {
                    "& .MuiPaper-root": {
                      backgroundColor: "#CFCFCF",
                      color: "#fff",
                    },
                  },
                },

                dialog: {
                  sx: {
                    "& .MuiPaper-root": {
                      backgroundColor: "#CFCFCF",
                      color: "#fff",
                    },
                  },
                },
              }}
            />
          </LocalizationProvider>
        </div>
      </ThemeProvider>
    </CacheProvider>
  );
}
